import { XIcon } from '@heroicons/react/solid';
import { Modal } from 'antd';
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';

const Plyr = dynamic(() => import('plyr-react'), { ssr: false });

import { logEvent } from '../../../../hooks/useClickEventTracking';
import { User } from '../../../../utils/interfaces/User';
import { ShouldRender } from '../../../shared/basic/ShouldRender';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  courseSampleLink?: string;
  courseId: string;
  userData: User | undefined;
}

const CoursePreviewModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  courseSampleLink,
  courseId,
  userData,
}) => {
  const startTimeRef = useRef<Date | null>(null);
  const wasOpenRef = useRef(false);

  const logLearnEvent = ({
    event_name,
    unit,
    value,
    activity_start,
    activity_end,
  }: {
    event_name: string;
    unit: 'boolean' | 'seconds';
    value: number;
    activity_start?: string;
    activity_end?: string;
  }) => {
    const eventParams: any = {
      event_name,
      source: 'courses',
      source_type: 'courses',
      source_id: courseId,
      sub_source: 'sample',
      unit,
      value,
      meta: {
        click_source: 'course_details_screen',
        click_action: 'watch preview',
      },
    };

    if (activity_start && activity_end) {
      eventParams.activity_start = activity_start;
      eventParams.activity_end = activity_end;
    }

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: window.location.pathname,
    });
  };

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      startTimeRef.current = new Date();




      logLearnEvent({
        event_name: 'learn_view',
        unit: 'boolean',
        value: 1,
      });
    }

    if (!open && wasOpenRef.current) {
      const end = new Date();
      const start = startTimeRef.current ?? end;
      const duration = Math.floor((end.getTime() - start.getTime()) / 1000);

      logLearnEvent({
        event_name: 'learn_session',
        unit: 'seconds',
        value: duration,
        activity_start: start.toISOString().slice(0, 19).replace('T', ' '),
        activity_end: end.toISOString().slice(0, 19).replace('T', ' '),
      });

      wasOpenRef.current = false;
      startTimeRef.current = null;
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      className='customModalNoPadding customLoginModal bg-none'
      closable={false}
      centered
      destroyOnHidden
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className='p-6 flex flex-col items-center text-white bg-[#1C1C1C] rounded-[16px]'
      >
        <div className='flex justify-between w-full items-center'>
          <h3 className='text-lg font-semibold mb-2'>Preview Course</h3>
          <XIcon
            onClick={onClose}
            className='w-5 h-5 text-white cursor-pointer'
          />
        </div>

        <div className='w-full flex justify-center'>
          <div className='w-full relative  h-full'>
            <ShouldRender check={courseSampleLink}>
              <Plyr
                source={{
                  type: 'video',
                  title: 'Course Preview',
                  sources: [
                    {
                      src: courseSampleLink as string,
                      type: 'video/mp4',
                    },
                  ],
                }}
                options={{
                  autoplay: true,
                  controls: [
                    'play',
                    'rewind',
                    'fast-forward',
                    'current-time',
                    'progress',
                    'mute',
                    'volume',
                    'captions',
                    'settings',
                  ],
                  settings: ['captions', 'quality', 'speed'],
                  keyboard: { focused: true, global: true },
                }}
              />
            </ShouldRender>
          </div>
        </div>
      </motion.div>
    </Modal>
  );
};

export default CoursePreviewModal;
